/**
 * Bona Pisca - Loading Spinner Components
 * Material 3 Enhanced - Mobile-first loading indicators optimized for maritime conditions
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useColors } from '../../contexts/ColorContext';
import { useSpringMotion } from '../../hooks/useSpringMotion';
import { Material3Button } from './Material3Button';
import { Material3Card } from './Material3Card';
import { Material3LoadingIndicator, Material3LoadingOverlay } from './Material3LoadingIndicators';

export interface LoadingSpinnerProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'white';
  className?: string;
  message?: string;
  fullScreen?: boolean;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  color = 'primary',
  className = '',
  message,
  fullScreen = false
}) => {
  const { dynamicColors } = useColors();
  const { t } = useTranslation('common');

  // Map old size system to new M3 size system
  const getM3Size = (oldSize: string) => {
    switch (oldSize) {
      case 'xs': case 'sm': return 'sm';
      case 'md': return 'md';
      case 'lg': case 'xl': return 'lg';
      default: return 'md';
    }
  };

  // Map old color system to new M3 color system
  const getM3Color = (oldColor: string) => {
    switch (oldColor) {
      case 'primary': return 'primary';
      case 'secondary': return 'secondary';
      case 'success': case 'warning': return 'tertiary';
      case 'danger': return 'surface'; // Use surface for error states
      case 'white': return 'surface';
      default: return 'primary';
    }
  };


  if (fullScreen) {
    return (
      <Material3LoadingOverlay
        isLoading={true}
        message={message || t('ui.loading.default')}
        type="wave" // Use marine-inspired wave loading for Mediterranean theme
        backdrop={true}
      />
    );
  }

  return (
    <div className={className}>
      <Material3LoadingIndicator
        type="wave" // Mediterranean marine-inspired loading
        size={getM3Size(size)}
        color={getM3Color(color)}
        message={message}
        marineTheme={true}
      />
    </div>
  );
};

// Specialized loading components for Mediterranean fishing contexts

export const FishingLoadingSpinner: React.FC<Omit<LoadingSpinnerProps, 'color'>> = ({
  message,
  ...props
}) => {
  const { t } = useTranslation('common');

  return (
    <Material3LoadingIndicator
      type="coral" // Marine-inspired coral loading for fishing context
      color="primary"
      message={message || t('ui.loading.fishingData')}
      marineTheme={true}
    />
  );
};

export const CatchLoadingSpinner: React.FC<Omit<LoadingSpinnerProps, 'color' | 'message'>> = () => {
  const { t } = useTranslation('common');

  return (
    <Material3LoadingIndicator
      type="tide" // Mediterranean tide loading for catch processing
      color="secondary"
      message={t('ui.loading.processingCatch')}
      marineTheme={true}
    />
  );
};

export const WeatherLoadingSpinner: React.FC<Omit<LoadingSpinnerProps, 'color' | 'message'>> = () => {
  const { t } = useTranslation('common');

  return (
    <Material3LoadingIndicator
      type="wave" // Wave loading for weather conditions
      color="primary"
      message={t('ui.loading.fetchingConditions')}
      marineTheme={true}
    />
  );
};

// Skeleton loading components for content placeholders
interface SkeletonProps {
  className?: string;
  width?: string;
  height?: string;
  rounded?: boolean;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  width = 'w-full',
  height = 'h-4',
  rounded = false
}) => (
  <div className={`${width} ${height} ${className}`}>
    <Material3LoadingIndicator
      type="skeleton"
      shape={rounded ? 'full' : 'medium'}
      marineTheme={true}
      className="w-full h-full"
    />
  </div>
);

// Mediterranean species card skeleton
export const SpeciesCardSkeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <Material3Card variant="elevated" shape="card-cozy" marineTheme={true} className={className}>
    <div className="p-6 space-y-4">
      <Skeleton height="h-24" rounded />
      <div className="space-y-2">
        <Skeleton height="h-5" width="w-3/4" />
        <Skeleton height="h-4" width="w-1/2" />
        <div className="flex justify-between">
          <Skeleton height="h-6" width="w-16" rounded />
          <Skeleton height="h-6" width="w-20" rounded />
        </div>
      </div>
    </div>
  </Material3Card>
);

// Catch card skeleton
export const CatchCardSkeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <Material3Card variant="elevated" shape="wave-gentle" marineTheme={true} className={className}>
    <div className="p-6 space-y-4">
      <Skeleton height="h-32" />
      <div className="space-y-3">
        <Skeleton height="h-5" width="w-2/3" />
        <Skeleton height="h-4" width="w-1/2" />
        <Skeleton height="h-3" width="w-1/3" />
        <div className="flex space-x-4">
          <Skeleton height="h-4" width="w-16" />
          <Skeleton height="h-4" width="w-16" />
        </div>
      </div>
    </div>
  </Material3Card>
);

// Weather card skeleton
export const WeatherCardSkeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <Material3Card variant="elevated" shape="coral-soft" marineTheme={true} className={className}>
    <div className="p-6 space-y-4">
      <div className="flex justify-between items-start">
        <div className="space-y-2">
          <Skeleton height="h-6" width="w-32" />
          <Skeleton height="h-4" width="w-24" />
        </div>
        <Skeleton height="h-10" width="w-16" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Skeleton height="h-4" width="w-20" />
        <Skeleton height="h-4" width="w-24" />
      </div>
      <Skeleton height="h-8" width="w-28" rounded />
    </div>
  </Material3Card>
);

// List skeleton for multiple items
interface ListSkeletonProps {
  count?: number;
  itemComponent?: React.ComponentType<{ className?: string }>;
  className?: string;
}

export const ListSkeleton: React.FC<ListSkeletonProps> = ({
  count = 3,
  itemComponent: ItemComponent = SpeciesCardSkeleton,
  className = ''
}) => (
  <div className={`space-y-4 ${className}`}>
    {Array.from({ length: count }).map((_, index) => (
      <ItemComponent key={index} />
    ))}
  </div>
);

// Full page loading with Mediterranean fishing theme
export const PageLoadingSpinner: React.FC<{ message?: string }> = ({
  message
}) => {
  const { t } = useTranslation('common');

  return (
    <Material3LoadingOverlay
      isLoading={true}
      message={message || t('ui.loading.app')}
      type="coral" // Mediterranean coral loading for app initialization
      backdrop={true}
    />
  );
};

// Loading state for data fetching
interface DataLoadingProps {
  isLoading: boolean;
  error?: string;
  retry?: () => void;
  children: React.ReactNode;
  skeleton?: React.ComponentType<any>;
  message?: string;
}

export const DataLoading: React.FC<DataLoadingProps> = ({
  isLoading,
  error,
  retry,
  children,
  skeleton: SkeletonComponent,
  message
}) => {
  const { dynamicColors } = useColors();
  const { t } = useTranslation('common');
  
  if (error) {
    return (
      <div className="text-center py-8 space-y-4">
        <div style={{ color: dynamicColors.error }}>
          <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.664-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <p 
            className="text-lg font-medium mb-2 transition-colors duration-200"
            style={{ color: dynamicColors.onSurface }}
          >
            {t('ui.loading.unableToLoad')}
          </p>
          <p 
            className="text-sm mb-4 transition-colors duration-200"
            style={{ color: dynamicColors.onSurfaceVariant }}
          >
            {error}
          </p>
        </div>
        {retry && (
          <Material3Button
            variant="filled"
            onClick={retry}
          >
            {t('actions.tryAgain')}
          </Material3Button>
        )}
      </div>
    );
  }

  if (isLoading) {
    if (SkeletonComponent) {
      return <SkeletonComponent />;
    }
    
    return (
      <div className="flex items-center justify-center py-8">
        <Material3LoadingIndicator
          type="tide" // Marine tide loading for data loading states
          size="lg"
          color="primary"
          message={message || t('ui.loading.default')}
          marineTheme={true}
        />
      </div>
    );
  }

  return <>{children}</>;
};

export default LoadingSpinner;
